/**
 * Fallback CSS-path walker — the safety net for the v1 element-selector
 * algorithm.
 *
 * When the orchestrator's strategy chain finds nothing usable across the full
 * ancestor walk, the engine invokes this fallback. It produces a positional
 * selector by walking up from the target to <html>, expressing each step as
 * either an id anchor (when one survives the autogen filter) or a
 * `tag:nth-of-type(n)` step.
 *
 * Key differences from the legacy autocapture `cssPath`:
 *
 *   1. Ids are filtered through `getStableId`, which honors the
 *      explicit-tracking-attribute suppression signal AND the autogenerated-id
 *      pattern pack. The legacy `cssPath` would happily anchor on `id=":r5:"`
 *      and produce selectors that never match across page loads.
 *
 *   2. Classes — when they're used at all for sibling disambiguation — are
 *      filtered through `filterClasses` against the unstable-class pattern
 *      pack. The current v1 implementation here doesn't emit classes (we lean
 *      entirely on `:nth-of-type` for sibling disambiguation), but the
 *      filtering helper is wired through so future iterations can opt back in
 *      surgically without re-deriving the policy.
 *
 *   3. A unique anchor id terminates the walk early — once we hit `body#main`,
 *      we don't need to keep walking up to `<html>`.
 *
 *   4. Ambiguous id anchors are skipped. Duplicate ids are invalid HTML but
 *      common enough in the wild that fallback must not emit selectors that
 *      resolve to the wrong target.
 *
 * Output format mirrors the orchestrator's output: `<anchor> > <descent>`.
 * When no usable id is found anywhere on the walk, the fallback emits a
 * pure-positional selector rooted at `html`.
 *
 * See the design doc:
 *   packages/plugin-autocapture-browser/element-selector-strategy-v1-no-classes.md
 */

import { ResolvedSelectorConfig } from './types';
import { normalizeResolvedConfig } from './config/resolve-config';
import { getStableId } from './helpers/get-stable-id';
import { escapeIdForCss } from './helpers/escape-id';
import { isShadowRoot, positionalStep, SHADOW_CHILD_CHAIN_PREFIX } from './helpers/shadow';

export interface FallbackCssPathOptions {
  /** Document or shadow root used for uniqueness checks. Defaults to the target's owner document. */
  scope?: ParentNode;
}

/**
 * Build a positional CSS selector for `el`, using stable ids as anchors when
 * available and falling back to `tag:nth-of-type(n)` for disambiguation.
 *
 * Honors `config.maxAncestorWalkDepth` defensively — once the depth limit is
 * reached, the walker stops and returns whatever it has built so far rooted at
 * the deepest reached ancestor. Exception: a shadow-root scope always walks to
 * the tree root (see `honorDepthCap`).
 *
 * A depth-capped light-DOM chain is relative and may match more than one
 * element — `resolveSelector` returns the first match, not necessarily the
 * target. Treat capped paths as locators, not identities.
 */
export function fallbackCssPath(
  el: Element,
  config: ResolvedSelectorConfig,
  options: FallbackCssPathOptions = {},
): string {
  const resolved = normalizeResolvedConfig(config);
  const scope: ParentNode = options.scope ?? el.ownerDocument ?? document;
  const scopeIsShadowRoot = isShadowRoot(scope as Node);
  const segments: string[] = [];
  let cursor: Element | null = el;
  let depth = 0;

  // The `SHADOW_CHILD_CHAIN_PREFIX` marker below is only emitted when the walk
  // terminates naturally, and it's the only thing anchoring a positional chain
  // inside a shadow root. Capping the walk there drops the marker, so
  // `resolveSelector` matches the first same-shaped subtree instead of the
  // target. The cap targets deep light DOMs; skipping it here is cheap.
  const honorDepthCap = !(resolved.shadowDomEnabled && scopeIsShadowRoot);

  while (cursor !== null) {
    if (honorDepthCap && resolved.maxAncestorWalkDepth !== undefined && depth > resolved.maxAncestorWalkDepth) {
      break;
    }

    const id = getStableId(cursor, resolved);
    if (id !== null) {
      // Anchor format mirrors the stableId strategy: `tag#id` with the id
      // CSS-escaped. Only terminate if the composed selector uniquely resolves
      // to the target; duplicate ids should fall through to positional steps.
      const anchorSegment = `${cursor.tagName.toLowerCase()}#${escapeIdForCss(id)}`;
      const candidate = [anchorSegment, ...segments].join(' > ');
      if (isUniqueMatch(scope, candidate, el)) {
        return candidate;
      }
    }

    // `positionalStep` emits `tag:nth-of-type(n)` for an element with a parent
    // and the bare `tag` for the document root. `config.shadowDomEnabled` also
    // lets it index a shadow-root-top element against its `ShadowRoot` siblings —
    // its `parentElement` is null, but it can have same-tag peers.
    segments.unshift(positionalStep(cursor, resolved.shadowDomEnabled));
    cursor = cursor.parentElement;
    depth += 1;
  }

  const path = segments.join(' > ');

  // In the light DOM the chain's first segment is `<html>` (unique), which
  // anchors it. Inside a shadow root there is no anchoring root element, so a
  // bare positional chain would match tree-wide. When we reached the top of the
  // tree naturally (`cursor === null`, not a depth-limit break), the chain spans
  // root → target via direct children — mark it so `resolveSelector` resolves it
  // by direct-child descent. The marker is explicit (not inferred from the step
  // syntax), so it stays correct even if steps later include classes.
  //
  // Requires `shadowDomEnabled`: the marker is only meaningful to a resolver that
  // is descending shadow roots, so a `ShadowRoot` scope alone doesn't produce it.
  if (resolved.shadowDomEnabled && scopeIsShadowRoot && cursor === null) {
    return `${SHADOW_CHILD_CHAIN_PREFIX}${path}`;
  }

  return path;
}

function isUniqueMatch(scope: ParentNode, selector: string, el: Element): boolean {
  let matches: NodeListOf<Element>;
  try {
    matches = scope.querySelectorAll(selector);
  } catch (_e) {
    return false;
  }
  return matches.length === 1 && matches[0] === el;
}
